"use client"

/**
 * Tenant catalog used to ship built-in products (Exam Management, Compliance,
 * …) with `source: "user"`. Those records are mirrored into `customProducts`,
 * and `resolveProductFromPath` prefers customs over built-ins — so
 * `/exam-management` became `exxat-custom` while Your App still listed the
 * built-in row. The built-in never matched `product`, so it never showed the
 * selected tick.
 *
 * Strip colliding mirrors once after hydrate and promote the active product to
 * the real built-in id when needed.
 */

import * as React from "react"

import { useProduct } from "@/contexts/product-context"
import {
  customProductSlugFromSuffix,
  customSuffixCollidesWithBuiltInProduct,
  resolveProductFromPath,
} from "@/lib/product-routing"

export function RepairBuiltinProductMirrors() {
  const {
    product,
    customProducts,
    activeCustomIndex,
    setProduct,
    setCustomProducts,
    setActiveCustomIndex,
  } = useProduct()

  React.useEffect(() => {
    const nextCustoms = customProducts.filter(
      (cp) => !customSuffixCollidesWithBuiltInProduct(cp.suffix)
    )

    if (product === "exxat-custom") {
      const active = customProducts[activeCustomIndex]
      if (active && customSuffixCollidesWithBuiltInProduct(active.suffix)) {
        const slug = customProductSlugFromSuffix(active.suffix)
        const builtin = resolveProductFromPath(`/${slug}/`, [])?.product
        if (builtin && builtin !== "exxat-custom") {
          setProduct(builtin)
          setActiveCustomIndex(0)
        }
      }
    }

    if (nextCustoms.length !== customProducts.length) {
      setCustomProducts(nextCustoms)
      if (activeCustomIndex >= nextCustoms.length) {
        setActiveCustomIndex(Math.max(0, nextCustoms.length - 1))
      }
    }
  }, [
    activeCustomIndex,
    customProducts,
    product,
    setActiveCustomIndex,
    setCustomProducts,
    setProduct,
  ])

  return null
}
